import type {
  CreateVendorContractTermTierRequest,
  VendorContractTermTierDto,
  VendorTierRateType,
} from "@/api/portfolioAdmin/types";

// Step 6 W4 — the pure half of the tier entry form on Update Terms.
//
// The ROW SHAPE deliberately mirrors the signed compensation schedule's
// editor (`components/Collection/vendorCompensation.ts`): staff type an
// "up to $X per period" ceiling per row and the last row is the open-ended
// top band. Staff read the schedule and key the terms, and asking them to
// re-express the same table two different ways is how transcription errors
// happen.
//
// The STORED shape is lower + inclusive upper, which is what makes the
// server's contiguity check possible. This module is the conversion between
// them, kept pure so the arithmetic can be pinned without a DOM.

/** One row as typed. Text, not numbers — a half-typed "1." is a legitimate
 *  intermediate state and must not round-trip through NaN. */
export interface TierRowInput {
  /** "Up to $X per period". Empty on the open-ended top row. */
  thresholdText: string;
  rateType: VendorTierRateType;
  /** A WHOLE percent ("7.5" = 7.5%) for Percent; dollars for Flat. */
  rateText: string;
}

export interface TierConversionResult {
  bands: CreateVendorContractTermTierRequest[];
  /** Null when the table is valid. The first problem, in reading order. */
  error: string | null;
}

export function makeTierRow(): TierRowInput {
  return { thresholdText: "", rateType: "Percent", rateText: "" };
}

/** The default table: one open-ended band, matching the compensation
 *  editor's seed. */
export function defaultTierRows(): TierRowInput[] {
  return [makeTierRow()];
}

function parseNumber(text: string): number | null {
  const cleaned = text.replace(/[$,%\s]/g, "");
  if (cleaned.trim() === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Convert typed rows into the bands the API stores, or report the first
 * problem.
 *
 * THE 100x BOUNDARY. A percent is typed as a whole number — "30" means 30% —
 * and stored as a FRACTION, matching the scalar PercentOfValue amount. The
 * conversion happens here and only here, and the variable names carry the
 * unit, because 30 reaching the store unconverted is a 3,000% rate that looks
 * entirely plausible on a screen showing "30".
 */
export function tierRowsToBands(rows: TierRowInput[]): TierConversionResult {
  const fail = (error: string): TierConversionResult => ({ bands: [], error });
  if (rows.length === 0) return fail("Add at least one band.");

  const bands: CreateVendorContractTermTierRequest[] = [];
  let lowerThreshold = 0;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const isTop = i === rows.length - 1;
    const label = `Band ${i + 1}`;

    const typedRate = parseNumber(row.rateText);
    if (typedRate === null) return fail(`${label}: enter a rate.`);
    if (typedRate < 0) return fail(`${label}: the rate cannot be negative.`);
    if (row.rateType === "Percent" && typedRate >= 100) {
      return fail(`${label}: a percentage must be below 100.`);
    }
    // Whole percent in, fraction out. See the note above.
    const rateAsFraction = row.rateType === "Percent" ? typedRate / 100 : typedRate;

    if (isTop) {
      bands.push({
        lowerThreshold,
        upperThreshold: null,
        rateType: row.rateType,
        rate: rateAsFraction,
      });
      break;
    }

    const upperThreshold = parseNumber(row.thresholdText);
    if (upperThreshold === null) {
      return fail(`${label}: enter the amount this band applies up to.`);
    }
    if (upperThreshold <= lowerThreshold) {
      return fail(
        `${label}: "up to" must be above ${lowerThreshold.toLocaleString("en-US")}, ` +
          "where the previous band ended.",
      );
    }

    bands.push({
      lowerThreshold,
      upperThreshold,
      rateType: row.rateType,
      rate: rateAsFraction,
    });
    // Contiguity is produced, not merely checked: each band starts exactly
    // where the previous one ended, so the server's guard can never fire on a
    // table this form built.
    lowerThreshold = upperThreshold;
  }

  return { bands, error: null };
}

/** Read stored bands back into rows, for showing an existing table. */
export function bandsToTierRows(bands: VendorContractTermTierDto[]): TierRowInput[] {
  if (bands.length === 0) return defaultTierRows();
  return [...bands]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((b) => ({
      thresholdText: b.upperThreshold == null ? "" : String(b.upperThreshold),
      rateType: b.rateType,
      rateText: b.rateType === "Percent" ? String(b.rate * 100) : String(b.rate),
    }));
}

/** "$0–$50,000 at 30%" / "above $100,000 at $1,500 flat". */
export function describeBand(band: VendorContractTermTierDto): string {
  const money = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const range =
    band.upperThreshold == null
      ? `above ${money(band.lowerThreshold)}`
      : `${money(band.lowerThreshold)}–${money(band.upperThreshold)}`;
  const priced =
    band.rateType === "Flat"
      ? `${money(band.rate)} flat`
      : `${(band.rate * 100).toLocaleString("en-US", { maximumFractionDigits: 4 })}%`;
  return `${range} at ${priced}`;
}
